import type { Countertop, Island, Kitchen } from '@rta/rta-blueprint-library';

import { getCountertopTopElevation } from './get-countertop-top-elevation';

/**
 * Do two islands' countertop slabs actually TOUCH, and where.
 *
 * A COLLISION TEST on the true slab coordinates, and nothing else: the drawn outlines in plan plus
 * the slabs' own thickness bands vertically. No connection, padding, overhang or material setting
 * takes part — a connection record says two islands were joined in the designer, not that their
 * tops meet. Shared by the adjoining SEAM markers and the CLEAN EDGE end labels so both agree.
 */

export type PlanPoint = { x: number; y: number };

export type CountertopBBox = { minX: number; maxX: number; minY: number; maxY: number; cx: number; cy: number };

/** Where two slabs meet, in the DISPLAYED island's local frame. */
export type CountertopContact = {
	/** Separating axis: 'x' is a junction at the displayed slab's side edge, 'y' at its front/back face. */
	axis: 'x' | 'y';
	/** Contact span along the other axis — the stretch over which the two slabs actually overlap. */
	start: number;
	end: number;
	displayed: CountertopBBox;
	other: CountertopBBox;
};

export type GetCountertopContactParams = {
	island: Island;
	neighbor: Island;
	kitchen: Kitchen;
	displayed: CountertopBBox;
};

// Touching means touching: the slab coordinates are exact, so the only slack is the model's own
// 1/16" precision. Anything wider is a real gap, whatever the connection/padding settings claim.
export const CONTACT_EPSILON = 0.0625;
// A corner-point graze is a collision but cannot be drawn as a seam; a junction needs real length.
export const MIN_CONTACT_SPAN = 1;

const DEGENERATE_EDGE = 1e-9;

/**
 * renderingDetails.vertices are ISLAND-LOCAL (each island's slab is centered on its own
 * origin — that is the frame the countertop sheet renders in). To compare two islands'
 * slabs, the neighbour's local vertices are lifted to world via its origin/axis, then
 * dropped into the DISPLAYED island's local frame via the inverse.
 */
export const toDisplayedLocal = (vertex: PlanPoint, neighbor: Island, displayed: Island): PlanPoint => {
	const tn = (neighbor.axis.y * Math.PI) / 180;
	const td = (displayed.axis.y * Math.PI) / 180;
	const wx = (vertex.x * Math.cos(tn)) - (vertex.y * Math.sin(tn)) + neighbor.origin.x;
	const wy = (vertex.x * Math.sin(tn)) + (vertex.y * Math.cos(tn)) + neighbor.origin.z;
	const dx = wx - displayed.origin.x;
	const dy = wy - displayed.origin.z;
	return {
		x: (dx * Math.cos(td)) + (dy * Math.sin(td)),
		y: (-dx * Math.sin(td)) + (dy * Math.cos(td))
	};
};

/** The drawn slab outline — one vertex per panel, overhang included (see getRenderingDetailsCfn). */
export const getDrawnPolygon = (countertop: Countertop, transform?: (vertex: PlanPoint) => PlanPoint): Array<PlanPoint> | null => {
	const rawVertices = countertop.renderingDetails?.vertices ?? [];
	if (rawVertices.length < 3) return null;
	return rawVertices.map((v) => (transform ? transform({ x: v.x, y: v.y }) : { x: v.x, y: v.y }));
};

const toBBox = (vertices: Array<PlanPoint>): CountertopBBox => {
	const xs = vertices.map((v) => v.x);
	const ys = vertices.map((v) => v.y);
	const [minX, maxX] = [Math.min(...xs), Math.max(...xs)];
	const [minY, maxY] = [Math.min(...ys), Math.max(...ys)];
	return { minX, maxX, minY, maxY, cx: (minX + maxX) / 2, cy: (minY + maxY) / 2 };
};

export const getDrawnBBox = (countertop: Countertop, transform?: (vertex: PlanPoint) => PlanPoint): CountertopBBox | null => {
	const vertices = getDrawnPolygon(countertop, transform);
	return vertices ? toBBox(vertices) : null;
};

type PlanEdge = [PlanPoint, PlanPoint];

const getEdges = (polygon: Array<PlanPoint>): Array<PlanEdge> =>
	polygon.map((vertex, i) => [vertex, polygon[(i + 1) % polygon.length]]);

const getCross = (o: PlanPoint, a: PlanPoint, b: PlanPoint): number => ((a.x - o.x) * (b.y - o.y)) - ((a.y - o.y) * (b.x - o.x));

const doEdgesCross = ([a1, a2]: PlanEdge, [b1, b2]: PlanEdge): boolean => {
	const d1 = getCross(b1, b2, a1);
	const d2 = getCross(b1, b2, a2);
	const d3 = getCross(a1, a2, b1);
	const d4 = getCross(a1, a2, b2);
	return ((d1 > 0) !== (d2 > 0)) && ((d3 > 0) !== (d4 > 0));
};

const getPointEdgeDistance = (point: PlanPoint, [a, b]: PlanEdge): number => {
	const vx = b.x - a.x;
	const vy = b.y - a.y;
	const lengthSq = (vx * vx) + (vy * vy);
	const projection = lengthSq < DEGENERATE_EDGE ? 0 : (((point.x - a.x) * vx) + ((point.y - a.y) * vy)) / lengthSq;
	const t = Math.max(0, Math.min(1, projection));
	return Math.hypot(point.x - (a.x + (t * vx)), point.y - (a.y + (t * vy)));
};

const isInsidePolygon = (point: PlanPoint, polygon: Array<PlanPoint>): boolean =>
	getEdges(polygon).reduce((inside, [a, b]) => {
		const straddles = (a.y > point.y) !== (b.y > point.y);
		if (!straddles) return inside;
		const crossingX = (((b.x - a.x) * (point.y - a.y)) / (b.y - a.y)) + a.x;
		return point.x < crossingX ? !inside : inside;
	}, false);

/** 0 when the two outlines intersect or one contains the other; otherwise the shortest distance between them. */
export const getPolygonGap = (a: Array<PlanPoint>, b: Array<PlanPoint>): number => {
	const edgesA = getEdges(a);
	const edgesB = getEdges(b);
	const collides = a.some((point) => isInsidePolygon(point, b))
		|| b.some((point) => isInsidePolygon(point, a))
		|| edgesA.some((edgeA) => edgesB.some((edgeB) => doEdgesCross(edgeA, edgeB)));
	if (collides) return 0;
	return Math.min(
		...a.flatMap((point) => edgesB.map((edge) => getPointEdgeDistance(point, edge))),
		...b.flatMap((point) => edgesA.map((edge) => getPointEdgeDistance(point, edge)))
	);
};

/** The slab's own vertical extent — its underside up to its top surface. */
const getSlabBand = (island: Island): { bottom: number; top: number } => {
	const top = getCountertopTopElevation(island);
	return { bottom: top - (island.getCountertop()?.material?.thickness ?? 0), top };
};

/** The stretch the two slabs share across the junction, or null when it is too short to seam. */
const getContactSpan = (axis: 'x' | 'y', displayed: CountertopBBox, other: CountertopBBox): { start: number; end: number } | null => {
	const start = axis === 'x' ? Math.max(displayed.minY, other.minY) : Math.max(displayed.minX, other.minX);
	const end = axis === 'x' ? Math.min(displayed.maxY, other.maxY) : Math.min(displayed.maxX, other.maxX);
	return end - start < MIN_CONTACT_SPAN ? null : { start, end };
};

/** Null when the slabs do not collide, or meet over too short a stretch to seam. */
export const getCountertopContact = (params: GetCountertopContactParams): CountertopContact | null => {
	const { island, neighbor, kitchen, displayed } = params;

	const [displayedCountertop] = kitchen.design.countertops.filterByIslandId(island.id);
	const [neighborCountertop] = kitchen.design.countertops.filterByIslandId(neighbor.id);
	if (!displayedCountertop || !neighborCountertop) return null;

	const displayedPolygon = getDrawnPolygon(displayedCountertop);
	const otherPolygon = getDrawnPolygon(neighborCountertop, (vertex) => toDisplayedLocal(vertex, neighbor, island));
	if (!displayedPolygon || !otherPolygon) return null;
	const other = toBBox(otherPolygon);

	// Vertically: the two slabs' own thickness bands have to meet, or they pass over/under each other.
	const displayedBand = getSlabBand(island);
	const otherBand = getSlabBand(neighbor);
	if (Math.min(displayedBand.top, otherBand.top) < Math.max(displayedBand.bottom, otherBand.bottom) - CONTACT_EPSILON) return null;

	// In plan: real outlines, not bounding boxes — a corner slab is a diagonal pentagon and an off-axis neighbour's box is larger than its slab.
	if (getPolygonGap(displayedPolygon, otherPolygon) > CONTACT_EPSILON) return null;

	// Which face the junction lands on: the separating axis is whichever the slabs share least.
	const gapX = Math.max(other.minX - displayed.maxX, displayed.minX - other.maxX);
	const gapY = Math.max(other.minY - displayed.maxY, displayed.minY - other.maxY);
	const axis = gapX >= gapY ? 'x' : 'y';

	const span = getContactSpan(axis, displayed, other);
	if (!span) return null;
	return { axis, start: span.start, end: span.end, displayed, other };
};
